import type { FileFullRepresentationsEntriesField } from 'box-typescript-sdk-gen/lib/schemas/fileFull.generated';
import type { ReactElement } from 'react';
import type { NextPageWithLayout } from 'src/pages/_app';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@savvy-kit/components/button';
import IconButton from '@savvy-kit/components/icon-button';
import { Spinner } from '@savvy-kit/components/spinner';
import { Text } from '@savvy-kit/foundations/text';
import PageTitle from '@savvy-kit/templates/page-title';
import { X } from 'lucide-react';
import mammoth from 'mammoth';
import toast from 'react-hot-toast';
import AdvisorDashTopNavLayout from 'src/components/advisor-dashboard/layout/advisor-dash-top-nav-layout';
import AssetDownloadFormats from 'src/components/advisor-dashboard/marketing/asset-download-formats';
import AssetDownloadSizes from 'src/components/advisor-dashboard/marketing/asset-download-sizes';
import AssetDownloadTags from 'src/components/advisor-dashboard/marketing/asset-download-tags';
import BoxContentPreviewer from 'src/components/files/box-content-preview';
import { graphql } from 'src/gql';
import { withAdvisorGuard } from 'src/hocs/with-advisor-guard';
import { createBoxClient } from 'src/utils/box-client';
import { downloadObjectUrl } from 'src/utils/download-file';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useTrackPageView } from '@savvy/deprecated-shared/tracking/use-track-page-view';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

const ACCESS_TOKEN = graphql(/* GraphQL */ `
  query AccessToken($userId: ID!, $resourceId: ID) {
    boxMarketingConfiguration(userId: $userId, resourceId: $resourceId) {
      accessToken
    }
  }
`);

const DOWNLOAD_BOX_FILE = graphql(/* GraphQL */ `
  mutation DownloadBoxFileUrl($input: DownloadBoxFileUrlInput!) {
    downloadBoxFileUrl(input: $input) {
      fileName
      url
    }
  }
`);

type DownloadBoxFileUrlInputType = {
  boxFileId: string;
  fileFormat: string;
  dimensions?: string;
};

export type FileInfo = {
  name: string;
  createdAt: Date;
  tags: string[];
  representations: FileFullRepresentationsEntriesField[];
  originalFormat: string;
};

function MarketingFilePreview() {
  useTrackPageView();
  const router = useRouter();
  const { user: authUser } = useAuth();
  const boxFileId = String(router.query['boxFileId']);

  const [buttonText, setButtonText] = useState('Download');
  const [secondaryButtonText, setSecondaryButtonText] = useState('Download');
  const [isDoc, setIsDoc] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [size, setSize] = useState('');
  const [fileInfo, setFileInfo] = useState<FileInfo>({
    name: '',
    createdAt: new Date(),
    tags: [],
    representations: [],
    originalFormat: '',
  });

  const [downloadBoxFile] = useMutationWithErrorLogging<
    { downloadBoxFileUrl: { fileName: string; url: string } },
    { input: DownloadBoxFileUrlInputType }
  >(DOWNLOAD_BOX_FILE);
  const { data: gqlData, loading } = useQueryWithErrorLogging(ACCESS_TOKEN, {
    variables: {
      userId: authUser?.id || '',
      resourceId: boxFileId,
    },
    onCompleted: (data) => {
      if (data.boxMarketingConfiguration.accessToken) {
        fetchBoxFileInfo(data.boxMarketingConfiguration.accessToken);
      } else {
        console.error('No access token available');
      }
    },
  });

  const accessToken = gqlData?.boxMarketingConfiguration.accessToken;

  const fetchBoxFileInfo = async (token: string) => {
    try {
      const boxClient = await createBoxClient(token);
      const boxFileInfo = await boxClient.files.getFileById(boxFileId, {
        queryParams: { fields: ['name', 'created_at', 'tags', 'representations'] },
      });

      const fileFormat = boxFileInfo.name?.split('.').pop() || '';
      setSelectedFormat(fileFormat);

      const filteredReps = boxFileInfo.representations?.entries?.filter((item) => {
        if (item.representation === 'jpg' || item.representation === 'png') {
          return item.properties?.dimensions === '1024x1024' || item.properties?.dimensions === '2048x2048';
        }
        return true;
      });

      setFileInfo({
        name: boxFileInfo.name ? boxFileInfo.name : 'Savvy File',
        createdAt: boxFileInfo.createdAt ? boxFileInfo.createdAt.value : new Date(),
        tags: Array.from(boxFileInfo.tags || []),
        representations: [...(filteredReps || [])],
        originalFormat: fileFormat,
      });

      setIsDoc(fileFormat === 'docx' || fileFormat === 'doc');
      setButtonText(fileFormat === 'docx' || fileFormat === 'doc' ? 'Copy to Clipboard' : 'Download');
    } catch (error) {
      console.error('Error fetching file info:', boxFileId, error);
    }
  };

  const fetchWithRetry = async (url: string, retryCount = 0): Promise<Response> => {
    try {
      return await fetch(url);
    } catch (error) {
      if (retryCount < 3) {
        console.error(`Retrying fetch for clipboard (attempt ${String(retryCount + 1)})`);
        const delay = 1000 * 2 ** retryCount;
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), delay);
        });
        return fetchWithRetry(url, retryCount + 1);
      }
      throw error;
    }
  };

  const handleDownload = async (copyToClipboard: boolean, secondaryButton: boolean, retryCount = 0) => {
    if (secondaryButton) setSecondaryButtonText('Downloading...');
    else {
      setButtonText(copyToClipboard ? 'Copying...' : 'Downloading...');
    }

    try {
      const { data } = await downloadBoxFile({
        variables: {
          input: {
            boxFileId,
            fileFormat: selectedFormat,
            dimensions: size,
          },
        },
      });
      if (!data?.downloadBoxFileUrl) {
        console.error('No data returned from downloadBoxFile', boxFileId);
        return;
      }

      if (copyToClipboard) {
        try {
          const response = await fetchWithRetry(data.downloadBoxFileUrl.url);
          const arrayBuffer = await response.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          if (document.hasFocus()) {
            await navigator.clipboard.write([
              new ClipboardItem({
                'text/html': new Blob([result.value], { type: 'text/html' }),
              }),
            ]);
            toast.success('Text copied to clipboard');
          } else {
            toast.error('Please make sure this window is active while copying and try again');
          }
        } catch (error) {
          handleMutationError(error, 'Error copying text, please try again');
          console.error('Error copying text to clipboard:', error);
        }
      } else {
        const { url, fileName } = data.downloadBoxFileUrl;
        downloadObjectUrl({
          objectUrl: url,
          filename: `${fileName}.${selectedFormat}`,
        });
        toast.success('File downloaded');
      }
    } catch (error) {
      if (retryCount < 3) {
        console.error(`Retrying download for file ${boxFileId} (attempt ${String(retryCount + 1)})`);
        const delay = 1000 * 2 ** retryCount;
        setTimeout(() => {
          handleDownload(copyToClipboard, secondaryButton, retryCount + 1);
        }, delay);
        return;
      }

      handleMutationError(error, 'Error downloading file, please try again');
      console.error('Error downloading file after retry:', boxFileId, error);
      setButtonText(copyToClipboard ? 'Copy to Clipboard' : 'Download');
    }

    if (secondaryButton) setSecondaryButtonText('Download');
    else {
      setButtonText(copyToClipboard ? 'Copy to Clipboard' : 'Download');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <PageTitle
        pageTitle="Marketing Center"
        breadcrumbs={[{ label: 'Marketing Center', href: '/advisor/dashboard/marketing' }, { label: 'Asset' }]}
        maxWidth="100%"
      />
      <div className="flex flex-row h-[calc(100vh_-_82px)]">
        <div className="flex flex-col justify-between gap-4 w-89 h-full border-r border-r-border-light">
          <div className="flex flex-col gap-4 max-w-full">
            <AssetDownloadTags fileTags={fileInfo.tags} />
            <AssetDownloadFormats
              fileInfo={fileInfo}
              selectedFormat={selectedFormat}
              setSelectedFormat={setSelectedFormat}
              setSize={setSize}
            />
            {(selectedFormat === 'jpg' || selectedFormat === 'png') && (
              <AssetDownloadSizes size={size} setSize={setSize} selectedFormat={selectedFormat} fileInfo={fileInfo} />
            )}
          </div>
          {fileInfo.name !== '' && (
            <div className="flex flex-col gap-4 px-4 py-6">
              {isDoc && (
                <Button variant="secondary" onClick={() => handleDownload(false, true)}>
                  {secondaryButtonText}
                </Button>
              )}
              <Button onClick={() => handleDownload(isDoc, false)}>{buttonText}</Button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4 flex-1">
          <div className="flex flex-row justify-between items-center pl-8 pr-6 pt-[14px] pb-0">
            <Text variant="h4">{fileInfo.name.replace(/\.[^/.]+$/, '').replace(/-/g, ' ') || 'Loading...'}</Text>
            <div className="flex flex-row gap-4 items-center">
              <Text variant="body-sm" color="secondary">
                {fileInfo.name ? (
                  <>
                    Uploaded on{' '}
                    {fileInfo.createdAt.toLocaleDateString('en-US', {
                      timeZone: 'UTC',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </>
                ) : (
                  ''
                )}
              </Text>
              <IconButton
                variant="ghost"
                onClick={() => {
                  if (window.history.length > 1) {
                    router.back();
                  } else {
                    router.push('/advisor/dashboard/marketing');
                  }
                }}
              >
                <X />
              </IconButton>
            </div>
          </div>
          <div className="h-full w-full bg-background-light">
            <BoxContentPreviewer accessToken={accessToken} fileId={boxFileId} showHeader={false} disableMaxWidth />
          </div>
        </div>
      </div>
    </>
  );
}

const MarketingFilePreviewPage: NextPageWithLayout = withAdvisorGuard(MarketingFilePreview);
const WrappedMarketingFilePreviewPage: NextPageWithLayout = MarketingFilePreviewPage;
WrappedMarketingFilePreviewPage.getLayout = function getLayout(page: ReactElement) {
  return <AdvisorDashTopNavLayout>{page}</AdvisorDashTopNavLayout>;
};
export default WrappedMarketingFilePreviewPage;
